<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-xl font-semibold mb-2">Sales Forecast - {{ currentMonthName }}</h2>
        <p class="text-gray-600">Prognoser och pipeline per säljare för innevarande månad</p>
      </div>
      <div class="flex gap-2">
        <button class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" @click="resetAllForecasts">
          <BaseIcon icon="undo" class="mr-2" /> Nolla alla
        </button>
        <button class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" :disabled="savedForecasts.length < 2" @click="showCompareDialog = true">
          <BaseIcon icon="code-branch" class="mr-2" /> Jämför prognoser
        </button>
        <button class="inline-flex items-center px-3 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700" @click="showSaveDialog = true">
          <BaseIcon icon="save" class="mr-2" /> Spara prognoser
        </button>
        <button class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" @click="showHistoryDialog = true">
          <BaseIcon icon="history" class="mr-2" /> Historik
        </button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center gap-2 mb-2">
          <BaseIcon icon="trophy" class="text-green-600" />
          <p class="text-sm text-gray-500">Vunna affärer</p>
        </div>
        <p class="text-2xl font-semibold">{{ formatNumber(totals.wonDeals) }} kr</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center gap-2 mb-2">
          <BaseIcon icon="crosshairs" class="text-emerald-600" />
          <p class="text-sm text-gray-500">Vunna+Commit</p>
        </div>
        <p class="text-2xl font-semibold">{{ formatNumber(totals.wonPlusCommit) }} kr</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center gap-2 mb-2">
          <BaseIcon icon="crosshairs" class="text-blue-600" />
          <p class="text-sm text-gray-500">Total Commit</p>
        </div>
        <p class="text-2xl font-semibold">{{ formatNumber(totals.commit) }} kr</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center gap-2 mb-2">
          <BaseIcon icon="chart-line" class="text-red-600" style="transform: scaleY(-1)" />
          <p class="text-sm text-gray-500">Total Worst Case</p>
        </div>
        <p class="text-2xl font-semibold">{{ formatNumber(totals.worstCase) }} kr</p>
      </div>
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center gap-2 mb-2">
          <BaseIcon icon="chart-line" class="text-green-600" />
          <p class="text-sm text-gray-500">Total Best Case</p>
        </div>
        <p class="text-2xl font-semibold">{{ formatNumber(totals.bestCase) }} kr</p>
      </div>
    </div>

    <!-- Main Forecast Table -->
    <div class="bg-white rounded-lg shadow p-6 overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b-2">
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #3273DC; min-width: 120px">Säljare</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #3273DC; min-width: 100px">Vunnen</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #3273DC; min-width: 100px">Commit</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #3273DC; min-width: 90px">Risk</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #0F2C5D; min-width: 100px">Strong best case</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #F1A239; min-width: 100px">Muntlig ök</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #F1A239; min-width: 110px">Offert/Andra möte</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #D4A017; min-width: 100px">Kvalificering</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #D4A017; min-width: 90px">Fallback</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #6B7280; min-width: 110px">Vunnen+Commit</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #6B7280; min-width: 100px">Worst case</th>
            <th class="text-center p-2 font-semibold text-xs text-white" style="background-color: #6B7280; min-width: 100px">Best case</th>
            <th class="text-center p-2 font-semibold text-xs" style="background-color: #D1D5DB; min-width: 90px">Förändr. V+C</th>
            <th class="text-center p-2 font-semibold text-xs" style="background-color: #D1D5DB; min-width: 90px">Förändr. WC</th>
            <th class="text-center p-2 font-semibold text-xs" style="background-color: #D1D5DB; min-width: 90px">Förändr. BC</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="seller in sellerPipelineData" :key="seller.name" class="border-b hover:bg-gray-50">
            <td class="p-2 font-medium text-left text-sm" style="background-color: #D0E2F7">{{ seller.name }}</td>
            <td class="p-2" style="background-color: #D0E2F7">
              <input type="number" :value="getForecast(seller.name).wonDeals !== undefined ? getForecast(seller.name).wonDeals : ''" :placeholder="formatNumber(wonDealsBySeller[seller.name] || 0)"
                class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                @input="updateForecast(seller.name, 'wonDeals', $event.target.value)" />
            </td>
            <td class="p-2" style="background-color: #D0E2F7">
              <div class="flex items-center gap-1">
                <input type="number" :value="getForecast(seller.name).commit || ''" placeholder="0"
                  class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                  @input="updateForecast(seller.name, 'commit', $event.target.value)" />
                <BaseIcon v-if="getForecast(seller.name).commit > 0 || (getEffectiveMuntlig(seller) + getEffectiveOffert(seller)) > 0"
                  :icon="isCommitTooHigh(seller) ? 'times-circle' : 'check-circle'"
                  :class="isCommitTooHigh(seller) ? 'text-red-600' : 'text-green-600'" class="flex-shrink-0" />
              </div>
            </td>
            <td class="p-2" style="background-color: #D0E2F7">
              <input type="number" :value="getForecast(seller.name).riskInCommit || ''" placeholder="0"
                class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                @input="updateForecast(seller.name, 'riskInCommit', $event.target.value)" />
            </td>
            <td class="p-2" style="background-color: #0F2C5D">
              <div class="flex items-center gap-1">
                <input type="number" :value="getForecast(seller.name).strongBestCase || ''" placeholder="0"
                  class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                  @input="updateForecast(seller.name, 'strongBestCase', $event.target.value)" />
                <BaseIcon v-if="getForecast(seller.name).riskInCommit > 0 || getForecast(seller.name).strongBestCase > 0"
                  :icon="isStrongBestCaseTooLow(seller) ? 'times-circle' : 'check-circle'"
                  :class="isStrongBestCaseTooLow(seller) ? 'text-red-600' : 'text-green-600'" class="flex-shrink-0" />
              </div>
            </td>
            <td class="p-2" style="background-color: #FCECD7">
              <input type="number" :value="getForecast(seller.name).muntlig !== undefined ? getForecast(seller.name).muntlig : ''" :placeholder="formatNumber(seller.muntlig)"
                class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                @input="updateForecast(seller.name, 'muntlig', $event.target.value)" />
            </td>
            <td class="p-2" style="background-color: #FCECD7">
              <input type="number" :value="getForecast(seller.name).offert !== undefined ? getForecast(seller.name).offert : ''" :placeholder="formatNumber(seller.offert)"
                class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                @input="updateForecast(seller.name, 'offert', $event.target.value)" />
            </td>
            <td class="p-2" style="background-color: #F5E6D3">
              <input type="number" :value="getForecast(seller.name).kvalificering !== undefined ? getForecast(seller.name).kvalificering : ''" :placeholder="formatNumber(seller.kvalificering)"
                class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                @input="updateForecast(seller.name, 'kvalificering', $event.target.value)" />
            </td>
            <td class="p-2" style="background-color: #F5E6D3">
              <input type="number" :value="getForecast(seller.name).ovrigt !== undefined ? getForecast(seller.name).ovrigt : ''" :placeholder="formatNumber(seller.ovrigt)"
                class="w-full text-right border rounded px-2 py-1 bg-white text-sm"
                @input="updateForecast(seller.name, 'ovrigt', $event.target.value)" />
            </td>
            <td class="text-right p-2 font-semibold text-sm" style="background-color: #E5E7EB">{{ formatNumber(getWonPlusCommit(seller.name)) }} kr</td>
            <td class="text-right p-2 font-semibold text-sm" :class="{ 'border-2 border-red-500': getWorstCase(seller.name) < 0 }" style="background-color: #E5E7EB">{{ formatNumber(getWorstCase(seller.name)) }} kr</td>
            <td class="text-right p-2 font-semibold text-sm" style="background-color: #E5E7EB">{{ formatNumber(getBestCase(seller.name)) }} kr</td>
            <td class="text-right p-2 text-sm" style="background-color: #F3F4F6">
              <span v-if="latestSavedForecast" class="flex items-center justify-end gap-1 font-semibold" :class="getChange(seller.name, 'wonPlusCommit') > 0 ? 'text-green-600' : getChange(seller.name, 'wonPlusCommit') < 0 ? 'text-red-600' : 'text-gray-600'">
                <BaseIcon :icon="getChange(seller.name, 'wonPlusCommit') > 0 ? 'chevron-up' : getChange(seller.name, 'wonPlusCommit') < 0 ? 'chevron-down' : 'minus'" class="text-xs" />
                {{ formatNumber(Math.abs(getChange(seller.name, 'wonPlusCommit'))) }}
              </span>
            </td>
            <td class="text-right p-2 text-sm" style="background-color: #F3F4F6">
              <span v-if="latestSavedForecast" class="flex items-center justify-end gap-1 font-semibold" :class="getChange(seller.name, 'worstCase') > 0 ? 'text-green-600' : getChange(seller.name, 'worstCase') < 0 ? 'text-red-600' : 'text-gray-600'">
                <BaseIcon :icon="getChange(seller.name, 'worstCase') > 0 ? 'chevron-up' : getChange(seller.name, 'worstCase') < 0 ? 'chevron-down' : 'minus'" class="text-xs" />
                {{ formatNumber(Math.abs(getChange(seller.name, 'worstCase'))) }}
              </span>
            </td>
            <td class="text-right p-2 text-sm" style="background-color: #F3F4F6">
              <span v-if="latestSavedForecast" class="flex items-center justify-end gap-1 font-semibold" :class="getChange(seller.name, 'bestCase') > 0 ? 'text-green-600' : getChange(seller.name, 'bestCase') < 0 ? 'text-red-600' : 'text-gray-600'">
                <BaseIcon :icon="getChange(seller.name, 'bestCase') > 0 ? 'chevron-up' : getChange(seller.name, 'bestCase') < 0 ? 'chevron-down' : 'minus'" class="text-xs" />
                {{ formatNumber(Math.abs(getChange(seller.name, 'bestCase'))) }}
              </span>
            </td>
          </tr>
          <!-- Totals Row -->
          <tr class="border-t-2 bg-gray-100 font-semibold">
            <td class="p-2 text-left text-sm font-bold" style="background-color: #A3C7F0">TOTALT</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #A3C7F0">{{ formatNumber(totals.wonDeals) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #A3C7F0">{{ formatNumber(totals.commit) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #A3C7F0">{{ formatNumber(totals.risk) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #3A7EBA; color: white">{{ formatNumber(totals.strongBestCase) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #F9D9A1">{{ formatNumber(totals.pipeline.muntlig) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #F9D9A1">{{ formatNumber(totals.pipeline.offert) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #E8D4A0">{{ formatNumber(totals.pipeline.kvalificering) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #E8D4A0">{{ formatNumber(totals.pipeline.ovrigt) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #D1D5DB">{{ formatNumber(totals.wonPlusCommit) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #D1D5DB">{{ formatNumber(totals.worstCase) }} kr</td>
            <td class="text-right p-2 text-sm font-bold" style="background-color: #D1D5DB">{{ formatNumber(totals.bestCase) }} kr</td>
            <td class="text-right p-2 text-xs" style="background-color: #F9FAFB" colspan="3">
              <span v-if="latestSavedForecast" class="text-gray-600">
                vs. {{ new Date(latestSavedForecast.date).toLocaleDateString('sv-SE') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Info Card -->
    <div class="bg-blue-50 rounded-lg p-4">
      <p class="text-sm text-gray-700">
        <strong>Information:</strong> Pipeline-data hämtas automatiskt från uppladdat data för innevarande månad.
        Fyll i Commit, Risk i Commit och Strong Best Case för varje säljare, och klicka sedan på "Spara prognoser".
      </p>
      <p class="text-sm text-gray-700 mt-2"><strong>Beräkningar:</strong></p>
      <ul class="text-sm text-gray-700 list-disc list-inside mt-1">
        <li>Vunnen+Commit = Vunna affärer + Commit Forecast</li>
        <li>Worst Case = Vunnen+Commit - Risk i Commit</li>
        <li>Best Case = Vunnen+Commit + Strong Best Case</li>
      </ul>
      <p class="text-sm text-gray-700 mt-2"><strong>Valideringsregler:</strong></p>
      <ul class="text-sm text-gray-700 list-disc list-inside mt-1">
        <li>Commit får inte vara större än summan av Muntlig överenskommelse + Offert / Andra möte</li>
        <li>Strong Best Case måste vara minst lika stor som Risk i Commit</li>
      </ul>
    </div>

    <!-- Save Dialog -->
    <modal name="save-forecast-modal" :width="425" height="auto" :scrollable="true">
      <div class="p-6">
        <h3 class="text-lg font-semibold mb-2">Spara Prognos</h3>
        <p class="text-sm text-gray-600 mb-4">Ange ett namn för denna prognos för att spara den som en snapshot.</p>
        <input type="text" v-model="forecastName" placeholder="Prognosnamn" class="w-full border rounded px-3 py-2 mb-4" />
        <div class="flex justify-end gap-2">
          <button class="px-4 py-2 border border-gray-300 rounded-md text-sm" @click="showSaveDialog = false">Avbryt</button>
          <button class="px-4 py-2 bg-primary-600 text-white rounded-md text-sm" @click="saveForecastSnapshot">Spara</button>
        </div>
      </div>
    </modal>

    <!-- History Dialog -->
    <modal name="history-forecast-modal" :width="800" height="auto" :scrollable="true">
      <div class="p-6">
        <h3 class="text-lg font-semibold mb-2">Prognoshistorik</h3>
        <p class="text-sm text-gray-600 mb-4">Välj en tidigare sparad prognos för att se detaljer eller ladda in den.</p>
        <div v-if="savedForecasts.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
          <div v-for="forecast in savedForecasts" :key="forecast.id" class="bg-white border rounded-lg p-4">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <h4 class="font-semibold text-lg">{{ forecast.name }}</h4>
                <p class="text-sm text-gray-500 mt-1">{{ new Date(forecast.date).toLocaleDateString('sv-SE', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) }}</p>
                <p class="text-sm text-gray-500">Månad: {{ forecast.monthYear }}</p>
                <div class="grid grid-cols-3 gap-4 mt-3">
                  <div><p class="text-xs text-gray-500">Total Commit</p><p class="font-semibold">{{ formatNumber(forecast.totals.commit) }} kr</p></div>
                  <div><p class="text-xs text-gray-500">Total Worst Case</p><p class="font-semibold">{{ formatNumber(forecast.totals.worstCase) }} kr</p></div>
                  <div><p class="text-xs text-gray-500">Total Best Case</p><p class="font-semibold">{{ formatNumber(forecast.totals.bestCase) }} kr</p></div>
                </div>
              </div>
              <div class="flex flex-col gap-2 ml-4">
                <button class="px-3 py-1 border border-gray-300 rounded text-sm" @click="loadSavedForecast(forecast)">Ladda</button>
                <button class="px-3 py-1 bg-red-600 text-white rounded text-sm" @click="deleteSavedForecast(forecast.id)">Radera</button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          <BaseIcon icon="history" class="text-4xl mb-2 opacity-50" />
          <p>Inga sparade prognoser ännu.</p>
        </div>
        <div class="mt-4 flex justify-end">
          <button class="px-4 py-2 border border-gray-300 rounded-md text-sm" @click="showHistoryDialog = false">Stäng</button>
        </div>
      </div>
    </modal>

    <!-- Compare Dialog -->
    <modal name="compare-forecast-modal" :width="1100" height="auto" :scrollable="true">
      <div class="p-6">
        <h3 class="text-lg font-semibold mb-2">Jämför Prognoser</h3>
        <p class="text-sm text-gray-600 mb-4">Välj två prognoser för att se skillnader.</p>
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label class="text-sm font-medium mb-2 block">Prognos 1 (Bas)</label>
            <select v-model="compareId1" class="w-full border rounded px-3 py-2">
              <option value="">Välj prognos 1</option>
              <option v-for="f in savedForecasts" :key="f.id" :value="f.id">{{ f.name }} ({{ new Date(f.date).toLocaleDateString('sv-SE') }})</option>
            </select>
          </div>
          <div>
            <label class="text-sm font-medium mb-2 block">Prognos 2 (Jämför)</label>
            <select v-model="compareId2" class="w-full border rounded px-3 py-2">
              <option value="">Välj prognos 2</option>
              <option v-for="f in savedForecasts" :key="f.id" :value="f.id" :disabled="f.id === compareId1">{{ f.name }} ({{ new Date(f.date).toLocaleDateString('sv-SE') }})</option>
            </select>
          </div>
        </div>
        <div v-if="comparisonResult" class="space-y-4">
          <div class="grid grid-cols-3 gap-4">
            <div class="rounded-lg p-4" style="background-color: #CFD5DF">
              <p class="text-xs text-gray-600 mb-2">Vunnen+Commit</p>
              <p class="text-lg font-semibold">{{ formatNumber(comparisonResult.forecast1.totals.wonPlusCommit || 0) }} kr</p>
              <p class="text-xs text-gray-500 mt-1">→ {{ formatNumber(comparisonResult.forecast2.totals.wonPlusCommit || 0) }} kr</p>
              <p class="text-sm font-medium mt-1" :class="comparisonResult.wonPlusCommitDiff >= 0 ? 'text-green-600' : 'text-red-600'">{{ comparisonResult.wonPlusCommitDiff >= 0 ? '+' : '' }}{{ formatNumber(comparisonResult.wonPlusCommitDiff) }} kr</p>
            </div>
            <div class="rounded-lg p-4" style="background-color: #D0E2F7">
              <p class="text-xs text-gray-600 mb-2">Worst Case</p>
              <p class="text-lg font-semibold">{{ formatNumber(comparisonResult.forecast1.totals.worstCase) }} kr</p>
              <p class="text-xs text-gray-500 mt-1">→ {{ formatNumber(comparisonResult.forecast2.totals.worstCase) }} kr</p>
              <p class="text-sm font-medium mt-1" :class="comparisonResult.worstCaseDiff >= 0 ? 'text-green-600' : 'text-red-600'">{{ comparisonResult.worstCaseDiff >= 0 ? '+' : '' }}{{ formatNumber(comparisonResult.worstCaseDiff) }} kr</p>
            </div>
            <div class="rounded-lg p-4" style="background-color: #FCECD7">
              <p class="text-xs text-gray-600 mb-2">Best Case</p>
              <p class="text-lg font-semibold">{{ formatNumber(comparisonResult.forecast1.totals.bestCase) }} kr</p>
              <p class="text-xs text-gray-500 mt-1">→ {{ formatNumber(comparisonResult.forecast2.totals.bestCase) }} kr</p>
              <p class="text-sm font-medium mt-1" :class="comparisonResult.bestCaseDiff >= 0 ? 'text-green-600' : 'text-red-600'">{{ comparisonResult.bestCaseDiff >= 0 ? '+' : '' }}{{ formatNumber(comparisonResult.bestCaseDiff) }} kr</p>
            </div>
          </div>

          <!-- Detailed Comparison Table -->
          <div class="bg-white border rounded-lg p-4 overflow-x-auto">
            <h4 class="font-semibold mb-4">Vunnen+Commit - Jämförelse per Säljare</h4>
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b-2">
                  <th class="text-left p-3">Säljare</th>
                  <th class="text-right p-3" style="background-color: #CFD5DF">V+C ({{ comparisonResult.forecast1.name }})</th>
                  <th class="text-right p-3" style="background-color: #CFD5DF">V+C ({{ comparisonResult.forecast2.name }})</th>
                  <th class="text-right p-3" style="background-color: #9CAABD">Diff</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="seller in comparisonResult.changedSellers" :key="seller.name" class="border-b hover:bg-gray-50">
                  <td class="p-3 font-medium">{{ seller.name }}</td>
                  <td class="text-right p-3">{{ formatNumber(seller.wonPlusCommit1) }} kr</td>
                  <td class="text-right p-3">{{ formatNumber(seller.wonPlusCommit2) }} kr</td>
                  <td class="text-right p-3 font-semibold" :class="seller.diff >= 0 ? 'text-green-600' : 'text-red-600'">{{ seller.diff >= 0 ? '+' : '' }}{{ formatNumber(seller.diff) }} kr</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="mt-4 flex justify-end">
          <button class="px-4 py-2 border border-gray-300 rounded-md text-sm" @click="showCompareDialog = false">Stäng</button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
const STORAGE_KEY = "sales_forecast_data";
const API_BASE = `${process.env.VUE_APP_SERVER_URL}/sales/pipeline`;

function normalizePhase(phase) {
  if (!phase) return "Okänd";
  const normalized = phase.trim();
  if (normalized.toLowerCase().includes("kvalificering")) return "Kvalificering";
  if (normalized.toLowerCase().includes("offert") || normalized.toLowerCase().includes("andra möte")) return "Offert / Andra möte";
  if (normalized.toLowerCase().includes("muntlig")) return "Muntlig överenskommelse";
  if (normalized.toLowerCase().includes("fallback") || normalized.toLowerCase().includes("övrigt")) return "Fallback";
  return normalized;
}

export default {
  name: "PipelineForecast",
  props: {
    data: { type: Array, required: true },
    wonDealsData: { type: Array, default: () => [] },
  },
  data() {
    return {
      forecasts: {},
      savedForecasts: [],
      showSaveDialog: false,
      showHistoryDialog: false,
      showCompareDialog: false,
      forecastName: "",
      compareId1: "",
      compareId2: "",
    };
  },
  computed: {
    currentMonthName() {
      return new Date().toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
    },
    currentMonthData() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      return this.data.filter((row) => {
        const dateStr = row["Avslutsdatum"];
        if (!dateStr) return false;
        const date = new Date(dateStr);
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      });
    },
    sellerPipelineData() {
      const sellerMap = {};
      this.currentMonthData.forEach((row) => {
        const seller = row["Säljare"] || "Okänd";
        const phase = normalizePhase(row["Fas"]);
        const value = Number(row["Ordervärde"]) || 0;
        if (!sellerMap[seller]) sellerMap[seller] = { muntlig: 0, offert: 0, kvalificering: 0, ovrigt: 0, total: 0 };
        if (phase === "Muntlig överenskommelse") sellerMap[seller].muntlig += value;
        else if (phase === "Offert / Andra möte") sellerMap[seller].offert += value;
        else if (phase === "Kvalificering") sellerMap[seller].kvalificering += value;
        else if (phase === "Fallback") sellerMap[seller].ovrigt += value;
        sellerMap[seller].total += value;
      });
      return Object.entries(sellerMap).map(([name, pipeline]) => ({ name, ...pipeline })).sort((a, b) => a.name.localeCompare(b.name, "sv-SE"));
    },
    wonDealsBySeller() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const wonMap = {};
      this.wonDealsData.forEach((row) => {
        const dateStr = row["Avslutsdatum"];
        if (!dateStr) return;
        const date = new Date(dateStr);
        if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
          const seller = row["Säljare"] || "Okänd";
          const value = Number(row["Ordervärde"]) || 0;
          wonMap[seller] = (wonMap[seller] || 0) + value;
        }
      });
      return wonMap;
    },
    totals() {
      const totalCommit = Object.values(this.forecasts).reduce((sum, f) => sum + (f.commit || 0), 0);
      const totalRisk = Object.values(this.forecasts).reduce((sum, f) => sum + (f.riskInCommit || 0), 0);
      const totalStrongBestCase = Object.values(this.forecasts).reduce((sum, f) => sum + (f.strongBestCase || 0), 0);
      const pipelineTotals = this.sellerPipelineData.reduce((acc, s) => {
        const forecast = this.forecasts[s.name];
        return {
          muntlig: acc.muntlig + (forecast && forecast.muntlig !== undefined ? forecast.muntlig : s.muntlig),
          offert: acc.offert + (forecast && forecast.offert !== undefined ? forecast.offert : s.offert),
          kvalificering: acc.kvalificering + (forecast && forecast.kvalificering !== undefined ? forecast.kvalificering : s.kvalificering),
          ovrigt: acc.ovrigt + (forecast && forecast.ovrigt !== undefined ? forecast.ovrigt : s.ovrigt),
          total: acc.total + s.total,
        };
      }, { muntlig: 0, offert: 0, kvalificering: 0, ovrigt: 0, total: 0 });
      const totalWonDeals = this.sellerPipelineData.reduce((sum, s) => {
        const forecast = this.forecasts[s.name];
        return sum + (forecast && forecast.wonDeals !== undefined ? forecast.wonDeals : (this.wonDealsBySeller[s.name] || 0));
      }, 0);
      const totalWonPlusCommit = totalWonDeals + totalCommit;
      return {
        commit: totalCommit,
        risk: totalRisk,
        strongBestCase: totalStrongBestCase,
        worstCase: totalWonPlusCommit - totalRisk,
        bestCase: totalWonPlusCommit + totalStrongBestCase,
        pipeline: pipelineTotals,
        wonDeals: totalWonDeals,
        wonPlusCommit: totalWonPlusCommit,
      };
    },
    latestSavedForecast() {
      if (this.savedForecasts.length === 0) return null;
      const sorted = [...this.savedForecasts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return sorted[0];
    },
    topSeller() {
      let maxCommit = 0;
      let topName = "-";
      Object.entries(this.forecasts).forEach(([seller, forecast]) => {
        if (forecast.commit > maxCommit) { maxCommit = forecast.commit; topName = seller; }
      });
      return { name: topName, commit: maxCommit };
    },
    comparisonResult() {
      if (!this.compareId1 || !this.compareId2) return null;
      const f1 = this.savedForecasts.find((f) => f.id === this.compareId1);
      const f2 = this.savedForecasts.find((f) => f.id === this.compareId2);
      if (!f1 || !f2) return null;

      const calcWPC = (forecast) => {
        if (forecast.totals && forecast.totals.wonPlusCommit != null) return forecast.totals.wonPlusCommit;
        return Object.values(forecast.forecasts || {}).reduce((sum, s) => sum + (s.wonDeals || 0) + (s.commit || 0), 0);
      };
      const wpc1 = calcWPC(f1);
      const wpc2 = calcWPC(f2);

      const allSellers = [...new Set([...Object.keys(f1.forecasts), ...Object.keys(f2.forecasts)])].sort();
      const changedSellers = allSellers.map((name) => {
        const s1 = f1.forecasts[name] || { wonDeals: 0, commit: 0 };
        const s2 = f2.forecasts[name] || { wonDeals: 0, commit: 0 };
        const v1 = (s1.wonDeals || 0) + (s1.commit || 0);
        const v2 = (s2.wonDeals || 0) + (s2.commit || 0);
        return { name, wonPlusCommit1: v1, wonPlusCommit2: v2, diff: v2 - v1 };
      }).filter((s) => s.diff !== 0);

      return {
        forecast1: { ...f1, totals: { ...f1.totals, wonPlusCommit: wpc1 } },
        forecast2: { ...f2, totals: { ...f2.totals, wonPlusCommit: wpc2 } },
        wonPlusCommitDiff: wpc2 - wpc1,
        worstCaseDiff: f2.totals.worstCase - f1.totals.worstCase,
        bestCaseDiff: f2.totals.bestCase - f1.totals.bestCase,
        changedSellers,
      };
    },
  },
  watch: {
    showSaveDialog(val) { val ? this.$modal.show("save-forecast-modal") : this.$modal.hide("save-forecast-modal"); },
    showHistoryDialog(val) { val ? this.$modal.show("history-forecast-modal") : this.$modal.hide("history-forecast-modal"); },
    showCompareDialog(val) { val ? this.$modal.show("compare-forecast-modal") : this.$modal.hide("compare-forecast-modal"); },
  },
  created() {
    this.loadForecasts();
  },
  methods: {
    formatNumber(num) {
      return (num || 0).toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    getForecast(seller) {
      return this.forecasts[seller] || { commit: 0, riskInCommit: 0, strongBestCase: 0 };
    },
    getEffectiveWonDeals(sellerName) {
      const f = this.forecasts[sellerName];
      return f && f.wonDeals !== undefined ? f.wonDeals : (this.wonDealsBySeller[sellerName] || 0);
    },
    getEffectiveMuntlig(seller) {
      const f = this.forecasts[seller.name];
      return f && f.muntlig !== undefined ? f.muntlig : seller.muntlig;
    },
    getEffectiveOffert(seller) {
      const f = this.forecasts[seller.name];
      return f && f.offert !== undefined ? f.offert : seller.offert;
    },
    getEffectiveKvalificering(seller) {
      const f = this.forecasts[seller.name];
      return f && f.kvalificering !== undefined ? f.kvalificering : seller.kvalificering;
    },
    getEffectiveOvrigt(seller) {
      const f = this.forecasts[seller.name];
      return f && f.ovrigt !== undefined ? f.ovrigt : seller.ovrigt;
    },
    getWonPlusCommit(sellerName) {
      return this.getEffectiveWonDeals(sellerName) + this.getForecast(sellerName).commit;
    },
    getWorstCase(sellerName) {
      const f = this.getForecast(sellerName);
      return this.getWonPlusCommit(sellerName) - f.riskInCommit;
    },
    getBestCase(sellerName) {
      const f = this.getForecast(sellerName);
      return this.getWonPlusCommit(sellerName) + f.strongBestCase;
    },
    getChange(sellerName, type) {
      if (!this.latestSavedForecast) return 0;
      const prev = this.latestSavedForecast.forecasts[sellerName];
      if (!prev) return type === 'wonPlusCommit' ? this.getWonPlusCommit(sellerName) : type === 'worstCase' ? this.getWorstCase(sellerName) : this.getBestCase(sellerName);
      const prevWon = prev.wonDeals !== undefined ? prev.wonDeals : 0;
      const prevWonPlusCommit = prevWon + (prev.commit || 0);
      const prevWorstCase = prevWonPlusCommit - (prev.riskInCommit || 0);
      const prevBestCase = prevWonPlusCommit + (prev.strongBestCase || 0);
      if (type === 'wonPlusCommit') return this.getWonPlusCommit(sellerName) - prevWonPlusCommit;
      if (type === 'worstCase') return this.getWorstCase(sellerName) - prevWorstCase;
      return this.getBestCase(sellerName) - prevBestCase;
    },
    isCommitTooHigh(seller) {
      const f = this.getForecast(seller.name);
      const maxAllowed = this.getEffectiveMuntlig(seller) + this.getEffectiveOffert(seller);
      return f.commit > maxAllowed;
    },
    isStrongBestCaseTooLow(seller) {
      const f = this.getForecast(seller.name);
      return f.strongBestCase < f.riskInCommit;
    },
    updateForecast(seller, field, value) {
      const numValue = parseFloat(value) || 0;
      const existing = this.forecasts[seller] || { commit: 0, riskInCommit: 0, strongBestCase: 0 };
      this.$set(this.forecasts, seller, { ...existing, [field]: numValue });
    },
    async loadForecasts() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) this.forecasts = JSON.parse(stored);
        const response = await this.axios.get(`${API_BASE}/forecasts`);
        if (response.data && response.data.success) {
          this.savedForecasts = response.data.data.map((f) => ({
            id: f._id,
            name: f.name,
            date: f.createdAt,
            monthYear: f.monthYear,
            forecasts: f.forecasts,
            totals: f.totals,
          }));
        }
      } catch (error) {
        console.error("Error loading forecast data:", error);
      }
    },
    resetAllForecasts() {
      this.forecasts = {};
      localStorage.setItem(STORAGE_KEY, JSON.stringify({}));
      this.$toasted.success("Alla prognoser nollställda", { duration: 3000 });
    },
    async saveForecastSnapshot() {
      if (!this.forecastName.trim()) {
        this.$toasted.error("Ange ett namn för prognosen", { duration: 3000 });
        return;
      }
      try {
        const payload = {
          name: this.forecastName.trim(),
          monthYear: this.currentMonthName,
          forecasts: this.buildCompleteForecasts(),
          totals: {
            commit: this.totals.commit,
            worstCase: this.totals.worstCase,
            bestCase: this.totals.bestCase,
            wonPlusCommit: this.totals.wonPlusCommit,
            wonDeals: this.totals.wonDeals,
          },
        };
        const response = await this.axios.post(`${API_BASE}/forecasts`, payload);
        if (response.data && response.data.success) {
          const saved = response.data.data;
          this.savedForecasts.unshift({
            id: saved._id,
            name: saved.name,
            date: saved.createdAt,
            monthYear: saved.monthYear,
            forecasts: saved.forecasts,
            totals: saved.totals,
          });
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.forecasts));
        this.forecastName = "";
        this.showSaveDialog = false;
        this.$toasted.success(`Prognos "${payload.name}" sparad`, { duration: 3000 });
      } catch (error) {
        console.error("Error saving forecast:", error);
        this.$toasted.error("Kunde inte spara prognosen", { duration: 3000 });
      }
    },
    async deleteSavedForecast(id) {
      try {
        await this.axios.delete(`${API_BASE}/forecasts/${id}`);
        this.savedForecasts = this.savedForecasts.filter((f) => f.id !== id);
        this.$toasted.success("Prognos raderad", { duration: 3000 });
      } catch (error) {
        console.error("Error deleting forecast:", error);
        this.$toasted.error("Kunde inte radera prognosen", { duration: 3000 });
      }
    },
    loadSavedForecast(forecast) {
      this.forecasts = { ...forecast.forecasts };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.forecasts));
      this.showHistoryDialog = false;
      this.$toasted.success(`Laddade prognos "${forecast.name}"`, { duration: 3000 });
    },
    buildCompleteForecasts() {
      const complete = {};
      this.sellerPipelineData.forEach((seller) => {
        const f = this.forecasts[seller.name] || {};
        complete[seller.name] = {
          commit: f.commit || 0,
          riskInCommit: f.riskInCommit || 0,
          strongBestCase: f.strongBestCase || 0,
          wonDeals: f.wonDeals !== undefined ? f.wonDeals : (this.wonDealsBySeller[seller.name] || 0),
          muntlig: f.muntlig !== undefined ? f.muntlig : seller.muntlig,
          offert: f.offert !== undefined ? f.offert : seller.offert,
          kvalificering: f.kvalificering !== undefined ? f.kvalificering : seller.kvalificering,
          ovrigt: f.ovrigt !== undefined ? f.ovrigt : seller.ovrigt,
        };
      });
      return complete;
    },
  },
};
</script>

<style scoped>
input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; appearance: textfield; }
</style>
